// clientsApi — typed CRUD for /v1/clients/* (Route 8, replaces the
// minimal lite version from Route 2).
import { apiFetch } from "../../lib/api-client.js";

export type ClientStatus = "active" | "inactive";

export interface ClientRow {
  id: string;
  tenant_id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  status: ClientStatus;
  site_count: number;
  created_at: string;
  updated_at: string;
}

export interface ClientLite { id: string; name: string }

export interface ClientListResponse {
  items: ClientRow[];
  total: number;
  correlation_id: string | null;
}

export interface ClientsQuery {
  tenantId?: string | null;
  q?: string | null;
  status?: "active" | "inactive" | "" | null;
  limit?: number | null;
  offset?: number | null;
}

export class ClientsApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[]; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyClientsError(err: unknown): string {
  if (err instanceof ClientsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改客戶資料。";
    if (err.status === 404) return "找不到此客戶。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    if (err.status === 400 && err.code === "invalid_body") {
      const f = (err.fields ?? []).join("、");
      return `欄位填寫不完整：${f}。`;
    }
    return "客戶資料暫時無法存取。";
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<ClientsApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined; let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string };
    code = body.error ?? code; cid = body.correlation_id ?? cid; fields = body.fields; msg = body.message;
  } catch {}
  return new ClientsApiError(r.status, code, cid, fields, msg);
}

/** Single-argument overload preserved for backwards-compat with older
 *  callers that only need tenant scoping; new code should pass a
 *  `ClientsQuery` object with limit/offset. */
export async function listClients(params?: ClientsQuery | string | null, qHint?: string | null): Promise<ClientListResponse> {
  // Legacy signature: listClients(tenantId, q?)
  const opts: ClientsQuery = typeof params === "string" || params === null || params === undefined
    ? { tenantId: params as string | null | undefined, q: qHint ?? null }
    : params;
  const qs = new URLSearchParams();
  if (opts.tenantId) qs.set("tenant_id", opts.tenantId);
  if (opts.q) qs.set("q", opts.q);
  if (opts.status) qs.set("status", opts.status);
  if (opts.limit != null) qs.set("limit", String(opts.limit));
  if (opts.offset != null) qs.set("offset", String(opts.offset));
  const r = await apiFetch(`/v1/clients${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ClientListResponse;
}

// Backwards-compat helper for sites picker (was listClientsLite).
export async function listClientsLite(tenantId?: string | null): Promise<{ items: ClientLite[]; correlation_id: string | null }> {
  // Pass a sane upper bound so the lite picker can list more than the
  // default 50 — most tenants have well under 200 clients and the
  // picker doesn't paginate, so a single oversized fetch is fine.
  const r = await listClients({ tenantId, limit: 200 });
  return { items: r.items.map((c) => ({ id: c.id, name: c.name })), correlation_id: r.correlation_id };
}

export interface CreateClientBody {
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  status?: ClientStatus;
}

export async function createClient(body: CreateClientBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/clients${qs}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type PatchClientBody = Partial<CreateClientBody>;
export async function patchClient(id: string, body: PatchClientBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/clients/${encodeURIComponent(id)}${qs}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body), credentials: "include" });
  if (!r.ok) throw await asError(r);
}

export async function archiveClient(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/clients/${encodeURIComponent(id)}${qs}`, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw await asError(r);
}
